import type { ReactNode } from 'react';
import { Fragment, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useApi, useMeta } from '@/lib/api';
import { cx } from '@/lib/cx';
import { fmtDateTime, fmtDay, fmtInt, fmtMoney, fmtPct, fmtSeconds, fmtTime, fmtTokens } from '@/lib/format';
import type { AskDetail, FeedItem, Step } from '@/lib/types';

import { AiOff, Empty, ErrorCard, PillTag } from './ui';

const TABS = [
  { key: 'steps', label: 'Steps' },
  { key: 'verdict', label: 'Verdict' },
  { key: 'next', label: 'What happened next' },
  { key: 'context', label: 'Context' },
  { key: 'raw', label: 'Raw' },
] as const;
type TabKey = (typeof TABS)[number]['key'];

const TYPE_LABEL: Record<FeedItem['type'], string> = {
  ask: 'ASK',
  action: 'PRODUCT ACTION',
  system_event: 'SYSTEM EVENT',
  run: 'RUN',
  mcp_call: 'MCP TOOL CALL',
};

function nodeClass(status: string): string {
  if (status === 'error') return 'bad';
  if (status === 'abandoned' || status === 'in_progress') return 'w';
  return 'ok';
}

function stepMeta(s: Step): string {
  return [
    s.model_label,
    s.latency_s !== null ? fmtSeconds(s.latency_s) : null,
    s.tokens_in || s.tokens_out ? `${fmtTokens(s.tokens_in)} in / ${fmtTokens(s.tokens_out)} out` : null,
    s.cache_share ? `${fmtPct(s.cache_share)} cached` : null,
    s.cost ? fmtMoney(s.cost) : null,
    s.attempts && s.attempts > 1 ? `${s.attempts - 1} ${s.attempts === 2 ? 'retry' : 'retries'}` : null,
    s.fallback_from ? `fell back from ${s.fallback_from}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
}

function Steps({ data }: { data: AskDetail }) {
  const { ask } = data;
  const askError = (ask.error_type || ask.error_message) && !data.steps.some((s) => s.error_type || s.error_message);
  return (
    <div className="tree">
      <div className="node u">
        <div className="nm">
          {ask.user
            ? (ask.user.name ?? ask.user.email ?? `User #${ask.user.id}`)
            : ask.channel === 'mcp'
              ? 'External AI'
              : 'Someone'}{' '}
          asked
        </div>
        <div className="mt">{ask.text ? `“${ask.text}”` : '(text cleared after 90 days)'}</div>
        {askError && (
          <div className="code bad">
            {[ask.error_step, ask.error_type, ask.error_message].filter(Boolean).join(': ')}
          </div>
        )}
      </div>
      {data.steps.map((s) => (
        <div
          key={s.id}
          className={cx('node', nodeClass(s.status), s.depth > 0 && 'child')}
          style={s.depth > 1 ? { marginLeft: s.depth * 18 } : undefined}
        >
          <div className="nm">{s.name}</div>
          <div className="mt">{stepMeta(s)}</div>
          {s.tool_input && <div className="code">input {s.tool_input}</div>}
          {(s.error_type || s.error_message) && (
            <div className="code bad">{[s.error_type, s.error_message].filter(Boolean).join(': ')}</div>
          )}
        </div>
      ))}
      {ask.answer_text && (
        <div className="node ok">
          <div className="nm">Replied</div>
          <div className="mt clamp2" title={ask.answer_text}>
            “{ask.answer_text}”
          </div>
        </div>
      )}
      {data.steps.length === 0 && <div className="muted">No AI calls were logged for this ask.</div>}
    </div>
  );
}

function Next({ data, onSelect }: { data: AskDetail; onSelect: (key: string) => void }) {
  const { events, summary } = data.next;
  return (
    <>
      {events.length === 0 && <div className="muted">Nothing else happened within 30 minutes.</div>}
      <div className="tree">
        {events.map((e, i) => (
          <div key={`${e.at}-${i}`} className={cx('node', e.kind === 'action' ? 'ok' : nodeClass(e.status))}>
            <div className="nm">
              {fmtTime(e.at)} · {e.label}
            </div>
            <div
              className={cx('mt', e.ask_id !== null && 'clickable')}
              onClick={e.ask_id !== null ? () => onSelect(`ask::${e.ask_id}`) : undefined}
            >
              {e.text ? (e.kind === 'action' ? e.text : `“${e.text}”`) : '—'}
            </div>
          </div>
        ))}
      </div>
      {summary && (
        <div className="verdict" style={{ marginTop: 8 }}>
          <b>{summary.recovered ? 'Recovered' : 'Gave up'}</b>
          {` after ${summary.rephrases} ${summary.rephrases === 1 ? 'rephrase' : 'rephrases'}, ${summary.minutes} ${summary.minutes === 1 ? 'minute' : 'minutes'} and ${fmtMoney(summary.cost)} in total.`}
        </div>
      )}
    </>
  );
}

function ContextTab({ data }: { data: AskDetail }) {
  const c = data.context;
  const co = c.company;
  const rows: [string, ReactNode][] = [
    ['Plan', co?.plan_key ?? '–'],
    ['Organisation', co?.organization_name ?? '–'],
    ['Customer since', co?.created_at ? fmtDay(co.created_at) : '–'],
    ['AI budget', co?.ai_budget_usd ? `${fmtMoney(co.ai_budget_usd)} a month` : 'none'],
    ['Users', fmtInt(c.members)],
    ['Asks, 30 days before', `${fmtInt(c.asks_30d_before)} (${fmtInt(c.failed_30d_before)} not ok)`],
    ['First AI ask', c.first_ask_at ? fmtDay(c.first_ask_at) : '–'],
    ...c.not_synced.map((k): [string, ReactNode] => [k, <span className="faint">not synced yet</span>]),
  ];
  return (
    <div className="kv">
      {rows.map(([k, v]) => (
        <Fragment key={k}>
          <span className="k">{k}</span>
          <span>{v}</span>
        </Fragment>
      ))}
    </div>
  );
}

function Raw({ data }: { data: AskDetail }) {
  const r = data.raw;
  const ids = r.llm_call_ids;
  const lines = [
    `llm_request_logs  ${ids.length ? `#${ids[0]} → #${ids[ids.length - 1]} (${ids.length} rows)` : 'none'}`,
    `session_id        ${r.session_id}`,
    `request_id        ${r.request_id ?? '–'}`,
    `request.prompt    ${r.prompt ? JSON.stringify(r.prompt) : '(cleared)'}`,
    `entity            ${r.entity ? `${r.entity.type} #${r.entity.id}` : '–'}`,
    `activity_event    ${r.activity_event_ids.length ? r.activity_event_ids.map((i) => `#${i}`).join(', ') : '–'}`,
  ];
  const download = () => {
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `ask-${data.ask.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <>
      <div className="code">{lines.join('\n')}</div>
      <span className="expand" onClick={download}>
        Download as JSON
      </span>
    </>
  );
}

function AskInspector({ id, onSelect }: { id: number; onSelect: (key: string) => void }) {
  const meta = useMeta();
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('steps');
  const query = useApi<AskDetail>(`/asks/${id}`);
  if (query.error) return <ErrorCard error={query.error} />;
  if (!query.data) return <div className="skel" style={{ height: 220 }} />;
  const data = query.data;
  const { ask } = data;
  return (
    <>
      <div className="faint" style={{ fontSize: 12, fontWeight: 600 }}>
        ASK #{ask.id} · {fmtDateTime(ask.at)}
      </div>
      <div style={{ fontWeight: 700, fontSize: 15, margin: '4px 0', overflowWrap: 'anywhere' }}>
        {ask.text ? `“${ask.text}”` : <span className="faint">(text cleared after 90 days)</span>}
      </div>
      <div className="muted" style={{ fontSize: 12 }}>
        {ask.company ? (
          <Link className="link" to={`/accounts/${ask.company.id}`}>
            {ask.company.name}
          </Link>
        ) : (
          'No company'
        )}
        {ask.user && (
          <>
            {' · '}
            <Link className="link" to={`/users/${ask.user.id}`}>
              {ask.user.name ?? ask.user.email ?? `User #${ask.user.id}`}
            </Link>
          </>
        )}
        {` · ${ask.area_label}`}
        {data.entity && ` · ${data.entity.type} #${data.entity.id}`}
      </div>
      {ask.pills.length > 0 && (
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap', marginTop: 6 }}>
          {ask.pills.map((p) => (
            <PillTag key={p.label} pill={p} />
          ))}
        </div>
      )}
      <div className="facts">
        <div>
          <div className="k">Duration</div>
          <div className="v">{fmtSeconds(ask.duration_s)}</div>
        </div>
        <div>
          <div className="k">Cost</div>
          <div className="v">{fmtMoney(ask.cost)}</div>
        </div>
        <div>
          <div className="k">AI calls</div>
          <div className="v">{fmtInt(ask.calls)}</div>
        </div>
        <div>
          <div className="k">Tokens</div>
          <div className="v">{fmtTokens(ask.tokens)}</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <button type="button" className="btn" disabled title="Opening a customer's company needs staff access: later">
          Open in Compass ↗
        </button>
        <button type="button" className="btn" onClick={() => navigator.clipboard?.writeText(window.location.href)}>
          Copy link
        </button>
        <button
          type="button"
          className="btn"
          disabled={data.issue_id === null}
          onClick={() => data.issue_id !== null && navigate(`/issues/${data.issue_id}`)}
        >
          View issue
        </button>
      </div>
      <div className="itabs">
        {TABS.map((t) => (
          <span key={t.key} role="button" className={cx(t.key === tab && 'on')} onClick={() => setTab(t.key)}>
            {t.label}
          </span>
        ))}
      </div>
      {tab === 'steps' && <Steps data={data} />}
      {tab === 'verdict' &&
        (meta.data?.ai_enabled ? (
          <Empty icon="✦" title="No verdict yet">
            This ask hasn't been labelled.
          </Empty>
        ) : (
          <AiOff what="The verdict" />
        ))}
      {tab === 'next' && <Next data={data} onSelect={onSelect} />}
      {tab === 'context' && <ContextTab data={data} />}
      {tab === 'raw' && <Raw data={data} />}
      <div className="audit">
        🔒{' '}
        {meta.data?.audit_enabled
          ? 'You opened a customer prompt. This view is recorded in the audit log.'
          : 'Local mode: opening customer prompts is not audited yet.'}
      </div>
    </>
  );
}

function EventDetail({ item }: { item: FeedItem }) {
  return (
    <>
      <div className="faint" style={{ fontSize: 12, fontWeight: 600 }}>
        {TYPE_LABEL[item.type]} · {fmtDateTime(item.at)}
      </div>
      <div style={{ fontWeight: 700, fontSize: 15, margin: '4px 0', overflowWrap: 'anywhere' }}>{item.text}</div>
      <div className="muted" style={{ fontSize: 12 }}>
        {item.company?.name ?? 'No company'}
        {item.actor && ` · ${item.actor}`}
        {item.area_label && ` · ${item.area_label}`}
      </div>
      <div className="facts">
        <div>
          <div className="k">Status</div>
          <div className="v">{item.status === 'error' ? 'failed' : item.status}</div>
        </div>
        <div>
          <div className="k">Duration</div>
          <div className="v">{fmtSeconds(item.duration_s)}</div>
        </div>
        <div>
          <div className="k">Cost</div>
          <div className="v">{fmtMoney(item.cost)}</div>
        </div>
        <div>
          <div className="k">Time</div>
          <div className="v">{fmtTime(item.at)}</div>
        </div>
      </div>
      <div className="note">Only AI asks have a step-by-step replay.</div>
    </>
  );
}

export function Inspector({
  selected,
  item,
  onSelect,
}: {
  selected: string;
  item: FeedItem | null;
  onSelect: (key: string) => void;
}) {
  const askId = selected.startsWith('ask::') ? Number(selected.slice('ask::'.length)) : null;
  if (askId) return <AskInspector key={askId} id={askId} onSelect={onSelect} />;
  if (item) return <EventDetail item={item} />;
  return (
    <Empty icon="▸" title="Select an event to see what happened">
      Use ↑ ↓ to move through the feed, and / to search.
    </Empty>
  );
}
